'use client'

import { useEffect, useRef, useCallback, useState } from 'react'
import { IconX } from '@tabler/icons-react'
import classes from './TwitchStream.module.css'

declare global {
  interface Window {
    Twitch?: {
      Embed: new (
        elementId: string,
        options: {
          width: number | string
          height: number | string
          channel?: string
          video?: string
          collection?: string
          parent: string[]
          autoplay?: boolean
          muted?: boolean
          theme?: 'light' | 'dark'
          layout?: 'video' | 'video-with-chat'
        },
      ) => {
        addEventListener: (event: string, callback: () => void) => void
        getPlayer: () => {
          play: () => void
          pause: () => void
          setVolume: (volume: number) => void
          setQuality: (quality: string) => void
          setMuted: (muted: boolean) => void
          seek: (timestamp: number) => void
        }
      }
    }
  }
}

export interface TwitchStreamProps {
  autoplay?: boolean
  muted?: boolean
  name?: string
  headline?: string
  teaser?: string
  body?: string
  buttonText?: string
  buttonUrl?: string
  videoId?: string
}

const STORAGE_KEY = 'twitchWidgetClosed'

function TwitchStream(props: TwitchStreamProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const embedRef = useRef<{
    addEventListener: (event: string, callback: () => void) => void
    getPlayer: () => {
      play: () => void
      pause: () => void
      setVolume: (volume: number) => void
      setQuality: (quality: string) => void
      setMuted: (muted: boolean) => void
      seek: (timestamp: number) => void
    }
  } | null>(null)
  const [isVisible, setIsVisible] = useState(() => {
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem(STORAGE_KEY) !== 'true'
    }
    return true
  })
  const [isScriptLoaded, setIsScriptLoaded] = useState(false)

  const handleClose = () => {
    setIsVisible(false)
    sessionStorage.setItem(STORAGE_KEY, 'true')
  }

  const loadTwitchScript = useCallback(() => {
    if (window.Twitch || isScriptLoaded) return Promise.resolve()

    return new Promise<void>((resolve) => {
      const script = document.createElement('script')
      script.src = 'https://embed.twitch.tv/embed/v1.js'
      script.onload = () => {
        setIsScriptLoaded(true)
        resolve()
      }
      document.body.appendChild(script)
    })
  }, [isScriptLoaded])

  useEffect(() => {
    const container = containerRef.current
    let embed: NonNullable<Window['Twitch']>['Embed']['prototype'] | null = null

    const createEmbed = async () => {
      if (!container || !isVisible) return

      await loadTwitchScript()

      if (window.Twitch && !embedRef.current) {
        // Clear container before creating new embed
        container.innerHTML = ''

        embed = new window.Twitch.Embed(container.id, {
          width: '100%',
          height: '100%',
          channel: props.videoId,
          autoplay: props.autoplay,
          muted: props.muted,
          theme: 'dark',
          parent: [window.location.hostname],
          layout: 'video',
        })

        embedRef.current = embed
      }
    }

    createEmbed()

    return () => {
      if (container) {
        container.innerHTML = ''
      }
      embedRef.current = null
    }
  }, [props.videoId, props.autoplay, props.muted, loadTwitchScript, isVisible])

  if (!isVisible) {
    return null
  }

  return (
    <div className={classes.streamContainer}>
      <div className={classes.streamHeader}>
        <div className={classes.streamHeaderText}>FC TV is broadcasting now</div>
        <span onClick={handleClose} title={props.body} className={classes.streamHeaderButton} aria-label="Close stream">
          <IconX />
        </span>
      </div>
      <div id={`twitch-embed-${props.videoId}`} ref={containerRef} className={classes.embedContainer} />
    </div>
  )
}

export default TwitchStream
